import sumBy from 'lodash/sumBy';

import { AggregatedTransactionReportView } from '@ndvr/reporting-api';

import { formatDate } from 'utils';
import { parseNumberStr } from 'utils/number-utils';

export interface MonthlyNetFlow {
    // Calendar month as YYYY-MM.
    yearMonth: string;
    // Sum of the positive amounts in the month (>= 0).
    inflow: number;
    // Sum of the negative amounts in the month, kept negative (<= 0) so the
    // chart bar plots below the zero axis directly.
    outflow: number;
    // Inflow plus outflow: what the month added on balance (negative when more left than came in).
    net: number;
}

// Buckets transactions by calendar month. Inflow vs outflow is judged by the
// amount's sign — never the type label — so the chart cannot contradict the ledger.
// Result is sorted ascending by month so it plots left→right.
export const monthlyNetFlow = (views: AggregatedTransactionReportView[]): MonthlyNetFlow[] => {
    const byMonth = new Map<string, MonthlyNetFlow>();

    for (const view of views) {
        // Reporting sends dates as "yyyy-MM-dd'Z'" (DateFormat.UTC_DATE_FORMATTER); formatDate
        // reads that trailing marker and reformats to the bucket key, and returns '' when the
        // row carries no date at all. It is also what the table renders with, so both surfaces
        // agree on which rows count.
        const yearMonth = formatDate(view.date, 'yyyy-MM');
        if (!yearMonth) {
            continue;
        }
        const amount = parseNumberStr(view.amount);
        const bucket = byMonth.get(yearMonth) ?? { yearMonth, inflow: 0, outflow: 0, net: 0 };
        if (amount >= 0) {
            bucket.inflow += amount;
        } else {
            bucket.outflow += amount;
        }
        bucket.net += amount;
        byMonth.set(yearMonth, bucket);
    }

    return Array.from(byMonth.values()).sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
};

// Totals over the monthly buckets, so the headline figures always agree with the chart: each
// figure is that field added up across the months (outflow stays negative).
export const netFlowTotals = (flows: MonthlyNetFlow[]) => ({
    inflow: sumBy(flows, 'inflow'),
    outflow: sumBy(flows, 'outflow'),
    net: sumBy(flows, 'net'),
});
